// PDF stipend invoice — a true downloadable .pdf (unlike the payslip, which is
// a print dialog). jsPDF is dynamically imported so the ~350 KB library stays
// out of the initial bundle and only loads when HR actually clicks Invoice.
//
// Amounts print as "Rs." — the rupee sign (₹, U+20B9) is not part of the
// WinAnsi encoding jsPDF's built-in fonts cover, and would render as garbage.
// The official Sasha logo (public/sasha-logo-small.png, the same asset the
// payslip uses) is embedded when it can be loaded; rendering degrades to a
// text-only header otherwise, never a failed download.

import type { MonthlyReport } from '@/types/monthly'

export interface StipendInvoiceEmployee {
  name?: string | null
  employeeId?: string | null
  role?: string | null
  department?: string | null
  position?: string | null
  email?: string | null
  assignedHrName?: string | null
}

export interface StipendInvoiceData {
  /** YYYY-MM the invoice covers. */
  month: string
  /** Human period label — the calculator's exact date range when known. */
  periodLabel: string
  finalized: boolean
  employee: StipendInvoiceEmployee
  invoiceNo: string
  invoiceDateLabel: string
  workingDays: number | null
  presentDays: number
  absentDays: number
  leaveDays: number | null
  allocatedSalary: number | null
  dailyRate: number | null
  earnedPay: number | null
  absenceDeduction: number | null
  reductions: number | null
  totalPay: number
}

const rs = (value: number | null | undefined) =>
  value == null ? '—' : `Rs. ${Math.round(value).toLocaleString('en-IN')}`

export function monthLabel(month: string): string {
  const [year, monthNumber] = String(month || '').split('-')
  const date = new Date(Number(year), Number(monthNumber) - 1, 1)
  return Number.isNaN(date.getTime())
    ? String(month || '-')
    : date.toLocaleString('en-US', { month: 'long', year: 'numeric' })
}

/**
 * Deterministic invoice number: one per employee per month, so the same
 * employee-period always re-issues the same number (traceable) while
 * different employees/months never collide.
 */
export function invoiceNumber(month: string, employeeId?: string | null, userId?: string): string {
  const seed = String(employeeId || userId || 'X').replace(/[^A-Za-z0-9]/g, '')
  const tail = (seed.length > 6 ? seed.slice(-6) : seed).toUpperCase()
  return `SI-${String(month || '').replace('-', '')}-${tail}`
}

/** Invoice data from a stored monthly report (the Monthly Stipends table). */
export function invoiceDataFromReport(report: MonthlyReport, employee: StipendInvoiceEmployee): StipendInvoiceData {
  const calc = report.attendanceCalc
  return {
    month: report.month,
    periodLabel: calc
      ? `${calc.startDate} → ${calc.endDate}`
      : monthLabel(report.month),
    finalized: report.finalStipend != null,
    employee,
    invoiceNo: invoiceNumber(report.month, employee.employeeId, report.userId),
    invoiceDateLabel: new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
    workingDays: report.attendance?.workingDays ?? calc?.workingDays ?? null,
    presentDays: calc?.presentDays ?? report.attendance?.present ?? 0,
    absentDays: calc?.absentDays ?? report.attendance?.absent ?? 0,
    leaveDays: report.attendance?.leave ?? null,
    allocatedSalary: calc?.allocatedSalary ?? null,
    dailyRate: calc?.dailyRate ?? null,
    earnedPay: calc?.earnedPay ?? null,
    absenceDeduction: calc?.absenceDeduction ?? null,
    reductions: calc?.reductions ?? null,
    totalPay: report.finalStipend ?? calc?.totalPay ?? report.suggestedStipend ?? 0,
  }
}

/** Invoice data from a saved HR calculator run (the per-employee history). */
export function invoiceDataFromCalculation(calc: {
  month: string
  startDate: string
  endDate: string
  workingDays?: number
  presentDays?: number
  absentDays?: number
  dailyRate?: number
  allocatedSalary: number
  absenceDeduction?: number
  earnedPay?: number
  reductions: number
  totalPay: number
}, employee: StipendInvoiceEmployee): StipendInvoiceData {
  return {
    month: calc.month,
    periodLabel: `${calc.startDate} → ${calc.endDate}`,
    finalized: true,
    employee,
    invoiceNo: invoiceNumber(calc.month, employee.employeeId),
    invoiceDateLabel: new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
    workingDays: calc.workingDays ?? null,
    presentDays: calc.presentDays ?? 0,
    absentDays: calc.absentDays ?? 0,
    leaveDays: null,
    allocatedSalary: calc.allocatedSalary ?? null,
    dailyRate: calc.dailyRate ?? null,
    earnedPay: calc.earnedPay ?? null,
    absenceDeduction: calc.absenceDeduction ?? null,
    reductions: calc.reductions ?? null,
    totalPay: calc.totalPay ?? 0,
  }
}

const ORANGE: [number, number, number] = [249, 115, 22]
const ORANGE_DARK: [number, number, number] = [194, 65, 12]
const INK: [number, number, number] = [17, 24, 39]
const MUTED: [number, number, number] = [107, 114, 128]
const RED: [number, number, number] = [190, 18, 60]
const GREEN: [number, number, number] = [4, 120, 87]

/**
 * Render the invoice onto a jsPDF document. Window-free on purpose so tests
 * can render the real document headlessly and assert the PDF bytes.
 */
export async function renderStipendInvoiceDoc(data: StipendInvoiceData, logoDataUrl?: string): Promise<any> {
  const { jsPDF } = await import('jspdf')
  const doc = new jsPDF({ unit: 'mm', format: 'a4' })
  const W = 210
  const M = 14 // page margin
  let y = 0

  // ── Header: logo + organization, invoice title block ────────────────────
  let textX = M
  if (logoDataUrl) {
    try {
      // Square official logo in a 16mm box, vertically centered in the header.
      const format = logoDataUrl.startsWith('data:image/jpeg') ? 'JPEG' : 'PNG'
      doc.addImage(logoDataUrl, format, M, 8, 16, 16)
      textX = M + 20
    } catch {
      // Unsupported image data — fall back to the text-only header.
      textX = M
    }
  }

  doc.setTextColor(...INK)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(16)
  doc.text('SASHA INFINITY', textX, 14)
  doc.setTextColor(...MUTED)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(7.5)
  doc.text('ATTENDANCE & STIPEND MANAGEMENT', textX, 19.5)

  doc.setTextColor(...ORANGE_DARK)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(13)
  doc.text('STIPEND INVOICE', W - M, 13, { align: 'right' })
  doc.setTextColor(...INK)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8.5)
  doc.text(`Invoice No: ${data.invoiceNo}`, W - M, 18.5, { align: 'right' })

  y = 28
  doc.setFillColor(...ORANGE)
  doc.rect(0, y, W, 1.6, 'F')
  y += 9

  // ── Billed To (left) + meta (right) ──────────────────────────────────────
  doc.setTextColor(...MUTED)
  doc.setFontSize(7.5)
  doc.setFont('helvetica', 'bold')
  doc.text('BILLED TO', M, y)
  doc.setTextColor(...INK)
  doc.setFontSize(11.5)
  doc.text(String(data.employee.name || '-'), M, y + 6)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8.5)
  const billedLines = [
    `Employee ID: ${data.employee.employeeId || '-'}`,
    [data.employee.role, data.employee.department, data.employee.position].filter(Boolean).join(' · ') || '-',
    data.employee.email || '',
    data.employee.assignedHrName ? `Assigned HR: ${data.employee.assignedHrName}` : '',
  ].filter(Boolean)
  billedLines.forEach((line, i) => doc.text(line, M, y + 11.5 + i * 4.4))

  const metaX = 128
  const metaRows: Array<[string, string]> = [
    ['Invoice date', data.invoiceDateLabel],
    ['Stipend period', data.periodLabel],
    ['Month', monthLabel(data.month)],
  ]
  doc.setFontSize(8.5)
  metaRows.forEach(([k, v], i) => {
    const ry = y + 1 + i * 5
    doc.setTextColor(...MUTED)
    doc.setFont('helvetica', 'normal')
    doc.text(k, metaX, ry)
    doc.setTextColor(...INK)
    doc.setFont('helvetica', 'bold')
    doc.text(v, W - M, ry, { align: 'right' })
  })
  // Status chip
  const statusY = y + 1 + metaRows.length * 5 + 1
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(7.5)
  const label = data.finalized ? 'FINALIZED' : 'PENDING REVIEW'
  const chipW = doc.getTextWidth(label) + 8
  doc.setFillColor(...(data.finalized ? GREEN : ORANGE))
  doc.roundedRect(W - M - chipW, statusY - 4, chipW, 6.5, 1.2, 1.2, 'F')
  doc.setTextColor(255, 255, 255)
  doc.text(label, W - M - chipW / 2, statusY + 0.6, { align: 'center' })

  y = Math.max(y + 11.5 + billedLines.length * 4.4, statusY + 6) + 6
  doc.setDrawColor(229, 231, 235)
  doc.setLineWidth(0.3)
  doc.line(M, y, W - M, y)
  y += 7

  // ── Attendance summary tiles ─────────────────────────────────────────────
  doc.setTextColor(...MUTED)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(8)
  doc.text('ATTENDANCE SUMMARY', M, y)
  y += 3.5
  const tiles: Array<[string, string]> = [
    ['Working days', data.workingDays == null ? '—' : String(data.workingDays)],
    ['Present days', String(data.presentDays)],
    ['Absent days', String(data.absentDays)],
    ['Leave (unpaid)', data.leaveDays == null ? '—' : String(data.leaveDays)],
  ]
  const tileW = (W - M * 2 - 9) / 4 // 4 tiles, 3mm gaps
  tiles.forEach(([label, value], i) => {
    const tx = M + i * (tileW + 3)
    doc.setFillColor(247, 247, 248)
    doc.setDrawColor(229, 231, 235)
    doc.roundedRect(tx, y, tileW, 15, 1.5, 1.5, 'FD')
    doc.setTextColor(...MUTED)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(6.8)
    doc.text(label.toUpperCase(), tx + tileW / 2, y + 5, { align: 'center' })
    doc.setTextColor(...(label === 'Absent days' && Number(value) > 0 ? RED : INK))
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(11)
    doc.text(value, tx + tileW / 2, y + 11.4, { align: 'center' })
  })
  y += 15 + 8

  // ── Stipend calculation breakdown ────────────────────────────────────────
  doc.setTextColor(...MUTED)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(8)
  doc.text('STIPEND CALCULATION', M, y)
  y += 3.5

  const rows: Array<[string, string, 'normal' | 'neg']> = [
    ['Monthly stipend (allocated)', rs(data.allocatedSalary), 'normal'],
    ['Daily stipend', data.dailyRate == null ? '—' : `${rs(data.dailyRate)} / day`, 'normal'],
    ['Days paid (present)', String(data.presentDays), 'normal'],
    ['Earned — present × daily', rs(data.earnedPay), 'normal'],
    ['Absence deduction', data.absenceDeduction == null ? '—' : data.absenceDeduction > 0 ? `- ${rs(data.absenceDeduction)}` : '—', 'neg'],
    ['Reductions', data.reductions == null ? '—' : data.reductions > 0 ? `- ${rs(data.reductions)}` : '—', 'neg'],
  ]
  doc.setFontSize(9)
  rows.forEach(([label, value, kind]) => {
    doc.setDrawColor(243, 244, 246)
    doc.line(M, y + 1.8, W - M, y + 1.8)
    doc.setTextColor(...(kind === 'neg' ? RED : MUTED))
    doc.setFont('helvetica', 'normal')
    doc.text(label, M, y)
    doc.setTextColor(...(kind === 'neg' ? RED : INK))
    doc.setFont('helvetica', 'bold')
    doc.text(value, W - M, y, { align: 'right' })
    y += 6.2
  })

  // Total row, highlighted.
  y += 1.5
  doc.setFillColor(255, 237, 213)
  doc.roundedRect(M, y - 4.5, W - M * 2, 12, 1.5, 1.5, 'F')
  doc.setTextColor(...ORANGE_DARK)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(10)
  doc.text('FINAL PAYABLE STIPEND', M + 3, y + 2.6)
  doc.setFontSize(13)
  doc.text(rs(data.totalPay), W - M - 3, y + 3, { align: 'right' })
  y += 16

  // ── Note + footer ────────────────────────────────────────────────────────
  doc.setTextColor(...MUTED)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(7.5)
  doc.text(
    'Final payable = Daily Stipend × Present Days − Reductions. Working days exclude weekly offs and official holidays;',
    M, y,
  )
  doc.text('approved leave is reported but unpaid. Figures reflect the HR stipend report at the time of generation.', M, y + 3.6)

  doc.setDrawColor(229, 231, 235)
  doc.line(M, 281, W - M, 281)
  doc.setFontSize(7)
  doc.setTextColor(...MUTED)
  doc.text('This is a system-generated invoice for official HR documentation.', M, 286)
  doc.text(
    `Generated ${new Date().toLocaleString('en-IN')}`,
    W - M, 286, { align: 'right' },
  )

  return doc
}

/** Fetch the official logo and convert it to a data URL jsPDF can embed.
 *  Downscaled to 192px via canvas — the 1563px original would embed as ~5 MB
 *  of raw pixels and bloat every invoice. DOM-guarded so the module still
 *  typechecks under the server tsconfig, which has no DOM lib. */
async function loadLogoDataUrl(): Promise<string | undefined> {
  try {
    const g = globalThis as any
    if (typeof g.window === 'undefined' || typeof g.fetch !== 'function' || typeof g.FileReader !== 'function') {
      return undefined
    }
    const res = await g.fetch(`${g.window.location.origin}/sasha-logo-small.png`)
    if (!res.ok) return undefined
    const blob = await res.blob()

    const rawDataUrl = await new Promise<string | undefined>(resolve => {
      const reader = new g.FileReader()
      reader.onload = () => resolve(String(reader.result) || undefined)
      reader.onerror = () => resolve(undefined)
      reader.readAsDataURL(blob)
    })

    // Downscale to a compact JPEG when canvas is available; the raw PNG
    // data URL stays as the fallback so the logo never silently disappears.
    try {
      if (typeof g.createImageBitmap === 'function' && typeof g.document !== 'undefined') {
        const bitmap = await g.createImageBitmap(blob)
        const canvas = g.document.createElement('canvas')
        canvas.width = 192
        canvas.height = 192
        const ctx = canvas.getContext('2d')
        if (ctx) {
          ctx.drawImage(bitmap, 0, 0, 192, 192)
          const jpeg = canvas.toDataURL('image/jpeg', 0.92)
          if (typeof jpeg === 'string' && jpeg.length > 100) return jpeg
        }
      }
    } catch {
      // fall through to the raw PNG
    }
    return rawDataUrl
  } catch {
    return undefined
  }
}

/** Render the invoice with the official logo and trigger the browser download. */
export async function exportStipendInvoicePdf(data: StipendInvoiceData): Promise<void> {
  const logoDataUrl = await loadLogoDataUrl()
  const doc = await renderStipendInvoiceDoc(data, logoDataUrl)
  const fileName = `${String(data.employee.name || 'Employee').replace(/\s+/g, '_')}_Stipend_Invoice_${data.month}.pdf`
  doc.save(fileName)
}
